import React from "react";
import produce from "immer";

import { logger } from "@jslib/shared/utils/logger";

import { useFlag } from "@/components/FeatureFlags/hooks";

import { type VerificationService } from "../types/types";

import { getFingerprintRequestId } from "./fingerprint";

/**
 * @description Initializes Fingerprint device data collection and adds the "Request ID"
 *              to metadata.
 */
export const useFingerprint = (verificationService: VerificationService): string | null => {
  const fingerprintEnabled = useFlag("fingerprint-device-data") === "fingerprint-enabled";
  const [requestId, setRequestId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const viewModel = verificationService?.viewModel;

    if (!fingerprintEnabled || !viewModel || viewModel.metadata?.fpSessionId) {
      return undefined;
    }

    let isMounted = true;

    const fetchAndSetRequestId = async () => {
      try {
        const id = await getFingerprintRequestId();

        if (!isMounted) {
          return;
        }

        setRequestId(id);

        if (verificationService.updateViewModel) {
          const updated = produce(viewModel, (draft) => {
            if (!draft.metadata) {
              draft.metadata = {};
            }
            draft.metadata.fpSessionId = id;
          });
          verificationService.updateViewModel(updated);
        }
      } catch (error) {
        logger.error(error, "Failed to set Fingerprint sessionId");
      }
    };

    fetchAndSetRequestId();

    return () => {
      isMounted = false;
    };
  }, [fingerprintEnabled, verificationService?.viewModel]);

  return requestId;
};
